/**
 * PageGrid — shared resizable/draggable grid.
 * Edit Layout + Widgets buttons live in the Header (admin only).
 * Non-admins see a read-only, non-draggable layout.
 */

import React, { useState, useCallback, useEffect } from "react";
import { Responsive ,useContainerWidth} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./PageGrid.css";
import { usePageGrid } from "../../context/PageGridContext";

//const ResponsiveGridLayout = useContainerWidth(Responsive);

const ADMIN_ROLES = ["admin"];

const isAdmin = () => ADMIN_ROLES.includes(localStorage.getItem("role"));

/* ── build default layouts ───────────────────────────────────────── */
const buildDefaultLayouts = (widgets) => {
  let y = 0;
  const lgItems = widgets.map((w) => {
    const item = { i: w.id, x: w.defaultX ?? 0, y: w.defaultY ?? y, w: w.defaultW ?? 12, h: w.defaultH ?? 5, minW: w.minW ?? 3, minH: w.minH ?? 3 };
    y += item.h; return item;
  });
  let ym = 0;
  const mdItems = widgets.map((w) => {
    const mw = Math.min(w.defaultW ?? 12, 10);
    const item = { i: w.id, x: 0, y: ym, w: mw, h: w.defaultH ?? 5, minW: w.minW ?? 3, minH: w.minH ?? 3 };
    ym += item.h; return item;
  });
  let ys = 0;
  const smItems = widgets.map((w) => {
    const sw = Math.min(w.defaultW ?? 12, 6);
    const item = { i: w.id, x: 0, y: ys, w: sw, h: w.defaultH ?? 5, minW: Math.min(w.minW ?? 3, 6), minH: w.minH ?? 3 };
    ys += item.h; return item;
  });
  let yx = 0;
  const xsItems = widgets.map((w) => {
    const item = { i: w.id, x: 0, y: yx, w: 4, h: w.defaultH ?? 5, minW: 2, minH: w.minH ?? 3 };
    yx += item.h; return item;
  });
  return { lg: lgItems, md: mdItems, sm: smItems, xs: xsItems };
};

const loadLayouts = (key, defaults) => { try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : defaults; } catch { return defaults; } };
const loadVisible = (key, defaults) => { try { const s = localStorage.getItem(key + "_w"); return s ? JSON.parse(s) : defaults; } catch { return defaults; } };

/* ════════════════════════════════════════════════════════════════════
   PageGrid
════════════════════════════════════════════════════════════════════ */
const PageGrid = ({ pageKey, widgets = [], allowAllUsers = false, extraPanelContent = null}) => {
const { ref, width } = useContainerWidth();

  const companyId = localStorage.getItem("companyid") || 1;
const lsKey = `pg_${companyId}_${pageKey}`;
  // const lsKey          = `pg_${pageKey}`;
  const defaultLayouts = buildDefaultLayouts(widgets);
  const defaultVisible = Object.fromEntries(widgets.map((w) => [w.id, w.defaultVisible !== false]));

  const [layouts,     setLayouts]     = useState(() => loadLayouts(lsKey, defaultLayouts));
  const [visible,     setVisible]     = useState(() => loadVisible(lsKey, defaultVisible));
  const [editMode,    setEditMode]    = useState(false);
  const [custOpen,    setCustOpen]    = useState(false);
  const [activeWidget,setActiveWidget]= useState(null);

  const admin = isAdmin();
  const ctx   = usePageGrid();

  /* register this grid with the header so header buttons work */
  useEffect(() => {
    if (!ctx) return;
    // if (!admin || !ctx) return;
    ctx.registerGrid({
      openEdit:    () => setEditMode(e => !e),
      openWidgets: () => { setCustOpen(true); setActiveWidget(null); },
      isEditOn:    () => editMode,
    });
    return () => ctx.unregisterGrid();
  }, [editMode]);

  const handleLayoutChange = useCallback((_cur, all) => {
    setLayouts(all);
    localStorage.setItem(lsKey, JSON.stringify(all));
  }, [lsKey]);

  const toggleWidget = (id) => {
    setVisible((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(lsKey + "_w", JSON.stringify(next));
      return next;
    });
  };

  const resetLayout = () => {
    setLayouts(defaultLayouts);
    setVisible(defaultVisible);
    localStorage.removeItem(lsKey);
    localStorage.removeItem(lsKey + "_w");
  };

  const activeIds        = widgets.filter((w) => visible[w.id] !== false).map((w) => w.id);
  const customizerWidgets = widgets.filter(w => w.customizer);

  return (
    <div className="pg-root" ref={ref}>

      {/* ── Edit banner (admin only, when edit mode on) ──────────── */}
      { editMode && (
        <div className="pg-edit-banner">
          <i className="mdi mdi-drag me-2" />
          <strong>Edit Mode</strong> — Drag tiles · Resize from corners · Place side-by-side · Click <strong>Save Layout</strong> in the topbar when done
          <button className="btn btn-sm btn-outline-light ms-auto" onClick={resetLayout}>
            <i className="mdi mdi-refresh me-1" />Reset
          </button>
        </div>
      )}

      {/* ── Non-admin read-only notice ───────────────────────────── */}
      {/* {!admin && (
        <div className="pg-readonly-notice">
          <i className="mdi mdi-lock-outline me-1" />
          View only — contact an admin to customize this layout
        </div>
      )} */}

      {/* ── Grid ────────────────────────────────────────────────── */}
      {activeIds.length === 0 ? (
        <div className="pg-empty">
          <i className="mdi mdi-view-dashboard-outline" />
          <p>No widgets — ask an admin to enable some.</p>
        </div>
      ) : (
        <Responsive
          className="pg-grid"
          layouts={layouts}
          onLayoutChange={handleLayoutChange}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
          rowHeight={55}
          isDraggable={ editMode}
          isResizable={editMode}
          draggableHandle=".pg-drag-handle"
          margin={[12, 12]}
          containerPadding={[0, 0]}
          useCSSTransforms
        >
          {activeIds.map((id) => {
            const w = widgets.find((x) => x.id === id);
            if (!w) return null;
            return (
              <div key={id} className={`pg-tile${editMode ? " pg-tile--edit" : ""}`}>
                <div className={`pg-tile-header${editMode ? " pg-drag-handle" : ""}`}>
                  <i className={`${w.icon || "mdi mdi-widgets-outline"} me-2`} style={{ fontSize: 13 }} />
                  <span className="pg-tile-label">{w.label}</span>
                  {/* Field customizer button — admin only */}
                  {w.customizer && !editMode && (
                    <button
                      className="pg-field-btn"
                      title="Customize fields"
                      onClick={() => { setActiveWidget(id); setCustOpen(true); }}
                    >
                      <i className="mdi mdi-table-edit" />
                    </button>
                  )}
                  {editMode && (
                    <span className="pg-drag-badge">
                      <i className="mdi mdi-drag-variant me-1" />drag/resize
                    </span>
                  )}
                </div>
                <div className="pg-tile-body">{w.content}</div>
              </div>
            );
          })}
        </Responsive>
      )}

      {/* ── Customizer offcanvas (admin only) ───────────────────── */}
      {custOpen && (
        <div className="offcanvas-backdrop fade show" onClick={() => setCustOpen(false)} style={{ zIndex: 1044 }} />
      )}
      {(
        <div
          className={`offcanvas offcanvas-end${custOpen ? " show" : ""}`}
          style={{ visibility: custOpen ? "visible" : "hidden", width: 340, zIndex: 1045 }}
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="offcanvas-title">
              <i className="mdi mdi-tune me-2 text-primary" />
              {activeWidget ? `Customize: ${widgets.find(w => w.id === activeWidget)?.label}` : "Customize Widgets"}
            </h5>
            <button type="button" className="btn-close" onClick={() => setCustOpen(false)} />
          </div>

          <div className="offcanvas-body" style={{ overflowY: "auto" }}>
            {activeWidget && (
              <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setActiveWidget(null)}>
                <i className="mdi mdi-arrow-left me-1" />Back to Widgets
              </button>
            )}

            {activeWidget ? (
              widgets.find(w => w.id === activeWidget)?.customizer
            ) : (
              <>
                <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                  Toggle widgets on/off. Use <strong>Edit Layout</strong> in the topbar to drag &amp; resize.
                  {customizerWidgets.length > 0 && (
                    <> Click <i className="mdi mdi-table-edit text-primary" /> on a widget to customize its fields.</>
                  )}
                </p>

                <div className="pg-section-title">WIDGETS</div>
                {widgets.map((w) => (
                  <div key={w.id} className={`pg-cust-row${visible[w.id] !== false ? " active" : ""}`}>
                    <div
                      className="d-flex align-items-center flex-grow-1"
                      style={{ gap: 10, cursor: "pointer" }}
                      onClick={() => toggleWidget(w.id)}
                    >
                      <div className="pg-cust-icon"><i className={w.icon || "mdi mdi-widgets-outline"} /></div>
                      <span className="flex-grow-1" style={{ fontSize: 13 }}>{w.label}</span>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input" type="checkbox"
                          checked={visible[w.id] !== false}
                          onChange={() => toggleWidget(w.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 32, height: 18, cursor: "pointer" }}
                        />
                      </div>
                    </div>
                    {w.customizer && (
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        style={{ fontSize: 11, padding: "2px 8px", flexShrink: 0 }}
                        onClick={() => setActiveWidget(w.id)}
                        title="Customize fields"
                      >
                        <i className="mdi mdi-table-edit me-1" />Fields
                      </button>
                    )}
                  </div>
                ))}

                <hr />
                {extraPanelContent && (
  <div style={{ marginTop: 4 }}>
    {extraPanelContent}
  </div>
)}

                <div className="d-flex gap-2 justify-content-center">
                  <button className="btn btn-outline-danger btn-sm" onClick={() => { resetLayout(); setCustOpen(false); }}>
                    <i className="mdi mdi-refresh me-1" />Reset All
                  </button>
                  <button className="btn btn-primary btn-sm px-4" onClick={() => setCustOpen(false)}>
                    <i className="mdi mdi-check me-1" />Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageGrid;
